import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import { ALL_QUOTATION_SEND_CHANNELS } from '@lims/shared-types';
import { quotationsApi } from '../../api/endpoints.js';
import type { QuotationLine } from '../../api/endpoints.js';
import { ApiError } from '../../api/client.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionBar,
  ActionButton,
  Alert,
  ConfirmDialog,
  DataTable,
  Dialog,
  ErrorState,
  LoadingState,
  Panel,
  ReadOnlyField,
  SaveErrorNotice,
  SelectField,
  StatusBadge,
  TextAreaField,
  saveFieldErrors,
  useToast,
  type DataColumn,
} from '../../components/ui/index.js';
import { formatDate, formatDateTime, formatMoney } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { displayName } from '../clients/bilingual.js';
import {
  ContextItem,
  Facts,
  RecordChain,
  RecordGrid,
  RecordHeader,
  RecordLink,
  RecordNumber,
} from '../clients/record-layout.js';
import { QuotationDocumentPanel, quotationDocumentKey } from './QuotationDocumentPanel.js';

/**
 * The quotation, as the client sees it — and the three acts that can follow.
 *
 * ===========================================================================
 * PRINT, DELIVER AND RECORD-THE-ANSWER ARE THREE SEPARATE BUTTONS,
 * THREE SEPARATE ENDPOINTS AND THREE SEPARATE PERMISSIONS.
 * ===========================================================================
 *
 * PRINT prints the quotation DOCUMENT — the server-rendered PDF, never this
 * page (D198) — and records that a copy was produced. It changes no status,
 * and the page says so beside the button rather than leaving anybody to
 * assume otherwise.
 * The print count is displayed precisely so the distinction is visible: a
 * quotation can read "printed 9 times" and "sent".
 *
 * DELIVER records that a PERSON put the document in the client's hands. This
 * product transmits nothing — there is no mail transport in it — so the dialog
 * asks how it was delivered rather than offering to send it.
 *
 * RECORD THE ANSWER is the only thing that moves a quotation to accepted or
 * rejected, and only a person can do it: nothing else in the system knows what
 * the client decided.
 *
 * Once it has left draft the quotation is read-only (D208). A change is a NEW
 * quotation: "Create New From This Quotation" (D209) makes an independent
 * draft and opens it for editing, with every difference named. A quotation
 * the client REJECTED may be deleted by a Manager with a reason (D210); a
 * deleted one shows who deleted it, when and why, and offers nothing but its
 * document.
 */
export function QuotationPreviewPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const { show } = useToast();
  const locale = currentLocale();

  const [sendOpen, setSendOpen] = useState(false);
  const [sendChannel, setSendChannel] = useState<string | null>(null);
  const [sendNote, setSendNote] = useState('');
  const [responseOpen, setResponseOpen] = useState(false);
  const [responseOutcome, setResponseOutcome] = useState<
    'accepted' | 'rejected'
  >('accepted');
  const [responseNote, setResponseNote] = useState('');
  const [copyOpen, setCopyOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteTried, setDeleteTried] = useState(false);

  const quotation = useQuery({
    queryKey: ['quotation', id],
    queryFn: () => quotationsApi.get(id as string),
  });

  /**
   * What an ACCEPTED quotation carries onward, and whether it already has.
   *
   * Phase 6 made this a fact rather than a promise: the API names the work
   * order raised from this quotation, or says plainly that none has been.
   * Asked only when the quotation is accepted, because the endpoint refuses
   * anything else — a quotation the client has not agreed to has nothing to
   * hand on.
   */
  const handover = useQuery({
    queryKey: ['quotation-handover', id],
    queryFn: () => quotationsApi.handover(id as string),
    enabled: quotation.data?.status === 'accepted',
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['quotation', id] });
    await queryClient.invalidateQueries({ queryKey: ['quotations'] });
    // Sending freezes the document; the next preview must show the frozen copy.
    await queryClient.invalidateQueries({ queryKey: quotationDocumentKey(id as string) });
  };

  const send = useMutation({
    mutationFn: () =>
      quotationsApi.send(id as string, {
        channel: sendChannel as string,
        ...(sendNote.trim() ? { note: sendNote.trim() } : {}),
      }),
    onSuccess: async () => {
      await invalidate();
      setSendOpen(false);
      show({ tone: 'success', message: t('quotations.sent') });
    },
  });

  const respond = useMutation({
    mutationFn: () =>
      quotationsApi.recordResponse(id as string, {
        outcome: responseOutcome,
        ...(responseNote.trim() ? { note: responseNote.trim() } : {}),
      }),
    onSuccess: async () => {
      await invalidate();
      setResponseOpen(false);
      show({ tone: 'success', message: t('quotations.responseRecorded') });
    },
  });

  /** A new, independent draft; the page moves to it, with what changed. */
  const copy = useMutation({
    mutationFn: () => quotationsApi.copy(id as string),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['quotations'] });
      setCopyOpen(false);
      show({
        tone: 'success',
        message: t('quotations.copy.done', { number: created.quotationNumber }),
      });
      navigate(`/quotations/${created.id}/edit`, {
        state: { copyNotes: created.copyNotes },
      });
    },
  });

  /** A soft delete with a reason. Nothing is erased (D210). */
  const remove = useMutation({
    mutationFn: () => quotationsApi.remove(id as string, { reason: deleteReason.trim() }),
    onSuccess: async () => {
      await invalidate();
      setDeleteOpen(false);
      show({ tone: 'success', message: t('quotations.deleted.done') });
    },
  });

  /** The reason's own problem: typed too short here, or refused by the server. */
  const deleteReasonError =
    deleteTried && deleteReason.trim().length < 3
      ? t('quotations.deleted.reasonRequired')
      : saveFieldErrors(remove.error, t).reason;

  if (quotation.isLoading) return <LoadingState label={t('common.loading')} />;
  if (quotation.error) {
    return (
      <ErrorState
        error={quotation.error}
        onRetry={() => void quotation.refetch()}
      />
    );
  }

  const record = quotation.data;
  if (!record) return null;

  const currency = record.currency;
  const money = (minor: number) => formatMoney(minor, { locale, currency });

  /** The client-facing texts, in the interface language where there are two. */
  const texts = (
    [
      [t('quotations.introduction'), displayName(record.introduction, locale)],
      [t('quotations.paymentTerms'), displayName(record.paymentTerms, locale)],
      [t('quotations.conditions'), displayName(record.terms, locale)],
      [t('quotations.scopeOfWork'), record.scopeOfWork],
      [t('quotations.remarks'), record.remarks],
      [t('quotations.additionalNotes'), record.additionalNotes],
    ] as const
  ).filter((entry): entry is readonly [string, string] => Boolean(entry[1]));

  const responseFailed =
    respond.error instanceof ApiError &&
    respond.error.details?.some(
      (detail) => detail.code === 'quotation_expired',
    );

  const columns: readonly DataColumn<QuotationLine>[] = [
    {
      key: 'description',
      header: t('quotations.lines'),
      render: (line) => (
        <div className="min-w-0">
          <span className="block text-default-900">
            {displayName(line.description, locale) ?? t('common.notRecorded')}
          </span>
          {line.referenceStandard ? (
            <span className="text-caption text-default-600" dir="ltr">
              {line.referenceStandard}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'quantity',
      header: t('quotations.quantity'),
      align: 'end',
      render: (line) => (
        <span dir="ltr" className="measure">
          {line.quantity}
        </span>
      ),
    },
    {
      key: 'unitPrice',
      header: t('quotations.unitPrice'),
      align: 'end',
      render: (line) => (
        <div className="text-end">
          <span className="measure block text-default-900" dir="ltr">
            {money(line.unitPriceMinor)}
          </span>
          {/*
            Where the price came from, beside the price. "This was the
            catalogue price" and "somebody with permission changed it" cannot
            be told apart from the number alone.
          */}
          <span className="text-caption text-default-600">
            {t(`priceSource.${line.priceSource}`)}
          </span>
        </div>
      ),
    },
    {
      key: 'total',
      header: t('quotations.total'),
      align: 'end',
      render: (line) => (
        <span className="measure text-default-900" dir="ltr">
          {money(line.totalMinor)}
        </span>
      ),
    },
  ];

  const notRecorded = t('common.notRecorded');
  const partyName = record.party.name ?? notRecorded;
  const raisedWorkOrder =
    handover.data?.workOrder.state === 'raised' ? handover.data.workOrder : null;

  return (
    <>
      <RecordHeader
        number={record.quotationNumber}
        badges={
          <>
            {/* The DERIVED state, exactly as the backend reports it. */}
            <StatusBadge
              label={t(`status.quotation.${record.effectiveStatus}`)}
              tone={
                record.effectiveStatus === 'accepted'
                  ? 'success'
                  : record.effectiveStatus === 'rejected' ||
                      record.effectiveStatus === 'expired'
                    ? 'warning'
                    : 'neutral'
              }
            />
            {record.isDeleted ? (
              <StatusBadge label={t('quotations.deleted.chip')} tone="danger" />
            ) : null}
          </>
        }
        title={record.subject ?? t('quotations.printTitle')}
        context={
          <>
            <ContextItem label={t('quotations.client')}>
              {record.party.kind === 'client' && record.party.clientId ? (
                <RecordLink to={`/clients/${record.party.clientId}`}>{partyName}</RecordLink>
              ) : (
                <bdi>{partyName}</bdi>
              )}
              {record.party.kind === 'prospect' ? (
                <span className="ms-1 text-default-600">{t('quotations.prospect')}</span>
              ) : null}
            </ContextItem>
            <ContextItem label={t('quotations.validUntil')}>
              {formatDate(record.validUntil, { locale })}
            </ContextItem>
          </>
        }
        breadcrumbs={[
          { label: t('nav.quotations'), to: '/quotations' },
          { label: record.quotationNumber },
        ]}
        actions={
          // A deleted quotation offers nothing but its document (D210).
          record.isDeleted ? null : (
          <ActionBar>
            {record.status !== 'draft' && can(PERMISSIONS.QUOTATIONS_CREATE) ? (
              <ActionButton
                priority="secondary"
                onPress={() => {
                  copy.reset();
                  setCopyOpen(true);
                }}
              >
                {t('quotations.copy.action')}
              </ActionButton>
            ) : null}
            {record.status === 'rejected' && can(PERMISSIONS.QUOTATIONS_DELETE) ? (
              <ActionButton
                priority="destructive"
                onPress={() => {
                  remove.reset();
                  setDeleteReason('');
                  setDeleteTried(false);
                  setDeleteOpen(true);
                }}
              >
                {t('quotations.deleted.action')}
              </ActionButton>
            ) : null}
            {record.isEditable && can(PERMISSIONS.QUOTATIONS_UPDATE) ? (
              <ActionButton
                priority="tertiary"
                onPress={() => navigate(`/quotations/${record.id}/edit`)}
              >
                {t('common.edit')}
              </ActionButton>
            ) : null}
            {record.status === 'draft' && can(PERMISSIONS.QUOTATIONS_SEND) ? (
              <ActionButton
                priority="primary"
                onPress={() => setSendOpen(true)}
              >
                {t('quotations.send')}
              </ActionButton>
            ) : null}
            {record.status === 'sent' &&
            can(PERMISSIONS.QUOTATIONS_RECORD_RESPONSE) ? (
              <ActionButton
                priority="primary"
                onPress={() => setResponseOpen(true)}
              >
                {t('quotations.response')}
              </ActionButton>
            ) : null}
          </ActionBar>
          )
        }
      />

      {/*
        Deleted: who, when and why, read-only. The record and its number stay;
        only the normal list leaves it out.
      */}
      {record.isDeleted ? (
        <div className="mb-stack">
          <Alert
            tone="warning"
            title={t('quotations.deleted.banner')}
            description={t('quotations.deleted.details', {
              name: record.deletedBy?.name ?? t('common.notRecorded'),
              when: formatDateTime(record.deletedAt, { locale }) ?? t('common.notRecorded'),
              reason: record.deletionReason ?? t('common.notRecorded'),
            })}
          />
        </div>
      ) : null}

      {/*
        Stated beside the controls, not buried in help text. This is the
        acceptance criterion made visible to the person using the page.
      */}
      <div className="mb-stack">
        <Alert tone="info" title={t('quotations.printHonesty')} />
      </div>

      {record.isExpired ? (
        <div className="mb-stack">
          <Alert tone="warning" title={t('quotations.expiredWarning')} />
        </div>
      ) : null}
      {responseFailed ? (
        <div className="mb-stack">
          <Alert tone="warning" title={t('quotations.expiredWarning')} />
        </div>
      ) : null}
      {record.prospectConversionPending ? (
        <div className="mb-stack">
          <Alert
            tone="warning"
            title={t('quotations.convertPending')}
            description={t('quotations.convertHint')}
            action={
              can(PERMISSIONS.CLIENTS_CREATE) ? (
                <ActionButton
                  priority="secondary"
                  size="sm"
                  onPress={() =>
                    navigate(`/quotations/${record.id}/convert`)
                  }
                >
                  {t('quotations.convert')}
                </ActionButton>
              ) : null
            }
          />
        </div>
      ) : null}

      <RecordGrid
        side={
          <>
            <Panel title={t('common.details')}>
              <Facts
                items={[
                  {
                    label: t('quotations.issueDate'),
                    value: formatDate(record.issueDate, { locale }),
                  },
                  {
                    label: t('quotations.validUntil'),
                    value: formatDate(record.validUntil, { locale }),
                  },
                  {
                    label: t('quotations.sentAt'),
                    value: record.sentAt
                      ? `${formatDate(record.sentAt, { locale })}${
                          record.sentChannel
                            ? ` — ${t(`sendChannel.${record.sentChannel}`)}`
                            : ''
                        }`
                      : notRecorded,
                  },
                  {
                    label: t('quotations.respondedAt'),
                    value: record.respondedAt
                      ? formatDate(record.respondedAt, { locale })
                      : notRecorded,
                  },
                  /*
                    Printed how many times, beside a status that has not moved.
                    The distinction made checkable on screen.
                  */
                  {
                    label: t('quotations.printCount'),
                    value: (
                      <span dir="ltr" className="measure">
                        {record.printCount}
                      </span>
                    ),
                  },
                  {
                    label: t('quotations.taxRate'),
                    value: (
                      <span dir="ltr" className="measure">
                        {record.taxRatePercent}
                      </span>
                    ),
                  },
                  {
                    label: t('quotations.contact'),
                    value: record.contact ? (
                      <>
                        <bdi>{record.contact.name}</bdi>
                        {record.contact.title ? (
                          <span className="block text-caption text-default-600">
                            <bdi>{record.contact.title}</bdi>
                          </span>
                        ) : null}
                      </>
                    ) : record.party.contactName ? (
                      <bdi>{record.party.contactName}</bdi>
                    ) : (
                      notRecorded
                    ),
                  },
                  {
                    label: t('quotations.inquiryReference'),
                    value: record.inquiryReference ? (
                      <bdi>{record.inquiryReference}</bdi>
                    ) : (
                      notRecorded
                    ),
                  },
                  {
                    label: t('quotations.location'),
                    value: record.projectLocation ? (
                      <bdi>{record.projectLocation.name}</bdi>
                    ) : (
                      notRecorded
                    ),
                  },
                  {
                    label: t('quotations.preparedBy'),
                    value: record.preparedBy ? (
                      <bdi>{record.preparedBy.displayName}</bdi>
                    ) : (
                      notRecorded
                    ),
                  },
                ]}
              />
            </Panel>

            {/*
              The chain this quotation sits in, as far as this page knows it:
              the client, the quotation it was created from (traceability
              only), and the work order raised from it.
            */}
            <Panel title={t('ordersRecord.chain')}>
              <RecordChain
                items={[
                  {
                    label: t('quotations.client'),
                    value:
                      record.party.kind === 'client' && record.party.clientId ? (
                        <RecordLink to={`/clients/${record.party.clientId}`}>
                          {record.party.clientNumber ?? partyName}
                        </RecordLink>
                      ) : (
                        <bdi>{partyName}</bdi>
                      ),
                  },
                  record.sourceQuotation
                    ? {
                        label: t('quotations.copy.source'),
                        value: (
                          <RecordLink
                            to={`/quotations/${record.sourceQuotation.id}`}
                            isNumber
                          >
                            {record.sourceQuotation.quotationNumber}
                          </RecordLink>
                        ),
                      }
                    : null,
                  {
                    label: t('ordersRecord.thisQuotation'),
                    value: <RecordNumber>{record.quotationNumber}</RecordNumber>,
                  },
                  raisedWorkOrder
                    ? {
                        label: t('workOrders.number'),
                        value: can(PERMISSIONS.WORK_ORDERS_VIEW) ? (
                          <RecordLink to={`/work-orders/${raisedWorkOrder.id}`} isNumber>
                            {raisedWorkOrder.workOrderNumber}
                          </RecordLink>
                        ) : (
                          <RecordNumber>{raisedWorkOrder.workOrderNumber}</RecordNumber>
                        ),
                      }
                    : null,
                ]}
              />
            </Panel>
          </>
        }
      >
        {/*
          The handover to the job.

          Two shapes that say different things: a NAMED work order that was
          raised from this quotation, or plainly none. Neither is inferred from
          an absence, and raising one is a deliberate act rather than something
          acceptance did on the client's behalf.
        */}
        {record.status === 'accepted' ? (
          <Panel
            title={t('quotations.handover')}
            description={t('quotations.handoverHint')}
          >
            {handover.isLoading ? (
              <LoadingState label={t('common.loading')} />
            ) : handover.data?.workOrder.state === 'raised' ? (
              <div className="flex flex-wrap items-center gap-inline">
                <span className="measure text-body font-semibold text-default-900" dir="ltr">
                  {handover.data.workOrder.workOrderNumber}
                </span>
                <span className="text-caption text-default-600">
                  {formatDate(handover.data.workOrder.raisedAt, { locale })}
                </span>
                {can(PERMISSIONS.WORK_ORDERS_VIEW) ? (
                  <ActionButton
                    priority="secondary"
                    size="sm"
                    onPress={() =>
                      navigate(
                        `/work-orders/${
                          handover.data?.workOrder.state === 'raised'
                            ? handover.data.workOrder.id
                            : ''
                        }`,
                      )
                    }
                  >
                    {t('quotations.openWorkOrder')}
                  </ActionButton>
                ) : null}
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-inline">
                <span className="text-body text-default-600">
                  {t('quotations.noWorkOrderYet')}
                </span>
                {can(PERMISSIONS.WORK_ORDERS_CREATE) ? (
                  <ActionButton
                    priority="primary"
                    size="sm"
                    onPress={() =>
                      navigate(`/work-orders/new?quotationId=${record.id}`)
                    }
                  >
                    {t('quotations.raiseWorkOrder')}
                  </ActionButton>
                ) : null}
              </div>
            )}
          </Panel>
        ) : null}

        <Panel title={t('quotations.lines')}>
          <DataTable
            label={t('quotations.lines')}
            columns={columns}
            rows={record.lines}
            rowKey={(line) => line.id}
            emptyContent={t('quotations.noLines')}
          />

          {/* The totals, at the trailing edge under the lines they sum. */}
          <div className="mt-stack flex justify-end border-t border-default-200 pt-stack">
            <dl className="grid w-full max-w-sm grid-cols-[minmax(0,1fr)_auto] gap-x-gutter gap-y-1 text-body">
              {(
                [
                  [t('quotations.subtotal'), record.totals.subtotalMinor],
                  [t('quotations.totalDiscount'), record.totals.discountMinor],
                  [t('quotations.taxable'), record.totals.taxableMinor],
                  [t('quotations.tax'), record.totals.taxMinor],
                ] as const
              ).map(([label, minor]) => (
                <div key={label} className="contents">
                  <dt className="text-default-600">{label}</dt>
                  <dd className="measure text-end text-default-900" dir="ltr">
                    {money(minor)}
                  </dd>
                </div>
              ))}
              <div className="contents">
                <dt className="border-t border-default-200 pt-1 font-semibold text-default-900">
                  {t('quotations.grandTotal')}
                </dt>
                <dd
                  className="measure border-t border-default-200 pt-1 text-end font-semibold text-default-900"
                  dir="ltr"
                >
                  {money(record.totals.totalMinor)}
                </dd>
              </div>
            </dl>
          </div>
        </Panel>

        {/* Preview, print and download: one server-rendered PDF (D198). */}
        <QuotationDocumentPanel quotation={record} />

        {texts.length > 0 || record.notes ? (
          <Panel title={t('quotations.commercialTitle')}>
            <div className="grid gap-stack">
              {texts.map(([label, value]) => (
                <ReadOnlyField key={label} label={label}>
                  <span className="whitespace-pre-line" dir="auto">
                    {value}
                  </span>
                </ReadOnlyField>
              ))}
              {record.notes ? (
                <ReadOnlyField label={t('quotations.notes')}>
                  <span className="whitespace-pre-line" dir="auto">
                    {record.notes}
                  </span>
                  <span className="block text-caption text-default-600">
                    {t('quotations.notesHint')}
                  </span>
                </ReadOnlyField>
              ) : null}
            </div>
          </Panel>
        ) : null}
      </RecordGrid>

      {/* Create New From This Quotation (D209). The source is not changed. */}
      <ConfirmDialog
        isOpen={copyOpen}
        onClose={() => setCopyOpen(false)}
        title={t('quotations.copy.title')}
        message={t('quotations.copy.confirm', { number: record.quotationNumber })}
        confirmLabel={t('quotations.copy.action')}
        confirmPriority="primary"
        isPending={copy.isPending}
        onConfirm={() => copy.mutate()}
      >
        <p className="mt-stack text-caption text-default-600">{t('quotations.copy.explain')}</p>
        {copy.error ? (
          <div className="mt-stack">
            <SaveErrorNotice error={copy.error} />
          </div>
        ) : null}
      </ConfirmDialog>

      {/* Delete a rejected quotation (D210): a reason, and nothing is erased. */}
      <Dialog
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title={t('quotations.deleted.title')}
        footer={
          <ActionBar>
            <ActionButton
              priority="tertiary"
              onPress={() => setDeleteOpen(false)}
              isDisabled={remove.isPending}
            >
              {t('common.cancel')}
            </ActionButton>
            <ActionButton
              priority="destructive"
              isLoading={remove.isPending}
              onPress={() => {
                setDeleteTried(true);
                if (deleteReason.trim().length < 3) return;
                remove.mutate();
              }}
            >
              {t('quotations.deleted.action')}
            </ActionButton>
          </ActionBar>
        }
      >
        <div className="grid gap-stack">
          <Alert
            tone="warning"
            title={t('quotations.deleted.confirm', { number: record.quotationNumber })}
            description={t('quotations.deleted.explain')}
          />
          {remove.error && !deleteReasonError ? (
            <SaveErrorNotice error={remove.error} />
          ) : null}
          <TextAreaField
            label={t('quotations.deleted.reason')}
            value={deleteReason}
            onChange={setDeleteReason}
            rows={3}
            textDirection="auto"
            isRequired
            {...(deleteReasonError ? { error: deleteReasonError } : {})}
          />
        </div>
      </Dialog>

      {/* Deliver. The dialog asks what a PERSON did. */}
      <Dialog
        isOpen={sendOpen}
        onClose={() => setSendOpen(false)}
        title={t('quotations.sendTitle')}
        footer={
          <ActionBar>
            <ActionButton
              priority="tertiary"
              onPress={() => setSendOpen(false)}
            >
              {t('common.cancel')}
            </ActionButton>
            <ActionButton
              priority="primary"
              isDisabled={!sendChannel}
              isLoading={send.isPending}
              onPress={() => send.mutate()}
            >
              {t('quotations.send')}
            </ActionButton>
          </ActionBar>
        }
      >
        <div className="grid gap-stack">
          <Alert tone="info" title={t('quotations.sendHonesty')} />
          <p className="text-caption text-default-600">{t('quotations.sendFreezes')}</p>
          <SelectField
            label={t('quotations.sendChannelLabel')}
            value={sendChannel}
            onChange={setSendChannel}
            isRequired
            options={ALL_QUOTATION_SEND_CHANNELS.map((value) => ({
              value,
              label: t(`sendChannel.${value}`),
            }))}
          />
          <TextAreaField
            label={t('quotations.sendNote')}
            value={sendNote}
            onChange={setSendNote}
            rows={2}
          />
        </div>
      </Dialog>

      {/* The client's actual answer. The only route to accepted or rejected. */}
      <Dialog
        isOpen={responseOpen}
        onClose={() => setResponseOpen(false)}
        title={t('quotations.responseTitle')}
        footer={
          <ActionBar>
            <ActionButton
              priority="tertiary"
              onPress={() => setResponseOpen(false)}
            >
              {t('common.cancel')}
            </ActionButton>
            <ActionButton
              priority="primary"
              isLoading={respond.isPending}
              onPress={() => respond.mutate()}
            >
              {t('common.save')}
            </ActionButton>
          </ActionBar>
        }
      >
        <div className="grid gap-stack">
          <Alert tone="info" title={t('quotations.responseHint')} />
          <SelectField
            label={t('quotations.responseTitle')}
            value={responseOutcome}
            onChange={(value) =>
              setResponseOutcome((value as 'accepted' | 'rejected') ?? 'accepted')
            }
            options={[
              { value: 'accepted', label: t('quotations.accept') },
              { value: 'rejected', label: t('quotations.reject') },
            ]}
          />
          <TextAreaField
            label={t('quotations.responseNote')}
            value={responseNote}
            onChange={setResponseNote}
            rows={3}
          />
        </div>
      </Dialog>
    </>
  );
}
